import Political from "model/political";
import { randomMessage } from "model/message";
import eatAction from "model/actions/eat";
import starve from "model/actions/health/starve";
import drinkAction from "model/actions/drink";
import parch from "model/actions/health/parch";
import die from "model/actions/health/die";
import Admin from "admin";
import Debug from "debug";
const debug = Debug("model:actions:takeStep");

const attempt = (context) => {
  const { map, political, party, terrain } = context;
  const result = {
    success: true,
    gain: {
      step: Admin().speed,
    },
  };

  const stepsToNextCity = Political.nextCityDistance(
    map,
    political,
    party.step,
  );
  if (stepsToNextCity < result.gain.step) {
    // Stop at the next city, even if we're going really fast
    result.gain.step = stepsToNextCity;
  }

  const currentStep = party.step + result.gain.step;

  const point = map.route[Math.min(currentStep, map.route.length - 1)];
  const position = map.h.mesh.vxs[point].map((x) => x * 1000);
  const arrived = currentStep >= map.route.length;

  debug(position, arrived);
  const politicalData = {
    ...Political.atStep(map, political, currentStep),
    attitude: party.attitude(political),
  };

  result.gain = {
    ...result.gain,
    ...politicalData,
    position,
    arrived,
  };

  debug(result);

  return result;
};

const consume = (context, consumeAction, consumePenaltyAction) => {
  const result = {
    context: {},
    haveSurvived: true,
  };

  const { success: canConsume, notification: actionWarning } = consumeAction
    .execute(context);
  if (canConsume) return { ...result, notification: actionWarning };

  const { success: haveSurvived, notification: penaltyWarning } =
    consumePenaltyAction.execute(context);
  return { ...result, haveSurvived, notification: penaltyWarning };
};

const drink = (context) => consume(context, drinkAction, parch);
const eat = (context) => consume(context, eatAction, starve);

const partyActions = (oldContext) => {
  const drinkResult = drink(oldContext);
  if (!drinkResult?.haveSurvived) return die.execute(oldContext);

  const eatResult = eat(oldContext);
  if (!eatResult?.haveSurvived) return die.execute(oldContext);

  return {
    context: {},
    notification: eatResult?.notification || drinkResult?.notification,
  };
};

/**
 * The only telling the player gets that a border ends a pursuit.
 *
 * There is no tooltip for this and there should not be: the rule is meant to be inferred from
 * having been chased across a country and then not chased. But it has to be *legible* at the
 * moment it happens, or the player reads the quiet afterwards as the dice going cold. One line,
 * on the step that crosses, and only when there was something to shake.
 *
 * None of these names the law or the leaving. What the man on the road can actually tell is
 * that the country behind him is empty.
 */
const shaken = [
  "the line is behind you now. nothing follows across it.",
  "whatever was back there stays back there.",
  "the road behind is empty and stays that way.",
];

const politcalNotification = ({ city, territory, crossedBorder, attitude }) => {
  debug(city, territory, crossedBorder, attitude);
  let msg = "";
  switch (attitude) {
    case Political.Hostile:
      msg = "tread lightly - they are enemies";
      break;
    case Political.Suspicious:
      msg = "tensions are running high";
      break;
    case Political.Neutral:
      msg = "they hold us no grudge";
      break;
    case Political.Friendly:
    default:
      msg = "it's good to be among friends";
  }

  if (crossedBorder) {
    return `now entering ${territory} ${msg}`;
  }

  if (city) return `entering the city known as ${city} - ${msg}`;
};

const executeSideEffects = (oldContext, result) => {
  const { party, calendar, terrain } = oldContext;
  const { history } = party;
  const { gain } = result;

  // Track action
  history.add({ action: "takeStep", result });
  history.delta(
    { step: gain.step, arrived: gain.arrived || undefined },
    oldContext,
  );
  calendar.nextStep(oldContext);

  // TODO: Move to party resource:
  party.takeStep();
  // Read before the crossing clears it — setPoliticalContext is what drops the pursuit.
  const wasPursued = party.isPursued();
  party.setPoliticalContext(gain.city, gain.territory, gain.crossedBorder);
  const shookPursuit = wasPursued && !party.isPursued();

  terrain.atStep(party.step + gain.step);
  debug(terrain.describeSituation());

  //const { context, notification: partyNotification } = partyActions(oldContext);
  let context = {};
  let partyNotification = null;
  // Shaking a pursuit outranks the terrain prose for the one step it happens on. It fires at
  // most once per flight and it is the payoff of the whole mechanic; the country will still be
  // there to describe on the next step.
  const notification = gain.arrived
    ? `arrived in ${gain.city}`
    : shookPursuit
    ? randomMessage(shaken)
    : partyNotification ?? terrain.describeSituation() ??
      politcalNotification(gain);

  return {
    context: { ...gain },
    notification,
  };
};

const takeStep = {
  title: "",
  enabled: () => true,
  execute: (oldContext) => {
    const result = attempt(oldContext);
    const { context, notification } = executeSideEffects(oldContext, result);

    return { context, notification };
  },
};

export default takeStep;
